import React, { useState, useMemo } from "react";
import Draggable from "react-draggable";
import { CloseOutlined } from "@ant-design/icons";
import { Modal, Button, Typography, Table, Popover } from "antd";
import { useQuery } from "react-query";
import { getEmployeeDayBreakdown } from "../../../api/server";
import { useIntl, IntlShape } from "react-intl";
import { getFullName } from "../../../components/util/helpers";
import moment from "moment";
import "moment/locale/he";
import {
  DayBreakdownItem,
  DayRecord,
  EmployeeRecord,
  AnalysisHoursModalProps,
  DayBreakdownTableProps,
  AnalysisQueryParams,
} from "../types";

const { Title } = Typography;

// Constants
const QUERY_CONFIG = {
  keepPreviousData: false,
  staleTime: 0,
};

/**
 * Formats minutes to hours (with 2 decimal places)
 * @param minutes - The number of minutes to format
 * @returns A string representing hours with 2 decimal places
 */
const formatMinutesToHours = (minutes?: number): string =>
  minutes ? (minutes / 60).toFixed(2) : "0.00";

/**
 * Component to display a detailed breakdown of an employee's day
 * Appears in a popover when hovering over a day
 */
const DayBreakdownTable: React.FC<DayBreakdownTableProps> = ({
  breakdown,
  intl,
}) => {
  if (!Array.isArray(breakdown)) return null;

  const columns = [
    {
      title: intl.formatMessage({ id: "app.salary.salry_symbol" }),
      dataIndex: "SymboType",
      key: "type",
      render: (text: string) =>
        intl.formatMessage({ id: `app.salary.${text}` }),
    },
    {
      title: intl.formatMessage({ id: "app.salary.start_time" }),
      dataIndex: ["Interval", "IntervalStart"],
      key: "start",
      render: (text: string) => moment(text).format("HH:mm"),
    },
    {
      title: intl.formatMessage({ id: "app.salary.end_time" }),
      dataIndex: ["Interval", "IntervalEnd"],
      key: "end",
      render: (text: string) => moment(text).format("HH:mm"),
    },
    {
      title: intl.formatMessage({ id: "app.salary.breakdown_duration" }),
      key: "duration",
      render: (_: any, record: DayBreakdownItem) => {
        const duration = moment
          .duration(
            moment(record.Interval.IntervalEnd).diff(
              moment(record.Interval.IntervalStart)
            )
          )
          .asHours()
          .toFixed(2);
        return duration;
      },
    },
  ];

  return (
    <Table
      size="small"
      columns={columns}
      dataSource={breakdown}
      pagination={false}
      style={{ maxWidth: 400 }}
      rowKey={(record, index) => `breakdown-${index}`}
    />
  );
};

/**
 * Modal component that displays detailed analysis of employee hours
 * Shows a breakdown of hours by week and day, with detailed information
 * about regular hours, extra hours at different rates, etc.
 *
 * The modal is draggable and displays data in a table format, grouped by weeks.
 * It includes week summaries and allows hovering over days to see detailed breakdowns.
 */
const AnalysisHoursModal: React.FC<AnalysisHoursModalProps> = ({
  employee,
  onHide,
  visible,
  activeYear,
  activeMonth,
  branch_id,
}) => {
  const [isDraggableDisabled, setIsDraggableDisabled] = useState<boolean>(true);
  const intl = useIntl();

  // Query parameters
  const queryParams = useMemo<AnalysisQueryParams>(
    () => ({
      current: 1,
      pageSize: 100,
      employeeId: employee?.id,
      branchId: branch_id,
      filter: {
        $and: [{ year: Number(activeYear) }, { month: Number(activeMonth) }],
      },
    }),
    [employee?.id, branch_id, activeYear, activeMonth]
  );
  // Data fetching
  const { isLoading, data } = useQuery<DayRecord[]>(
    [{ params: queryParams, visible }],
    async () => {
      const result = await getEmployeeDayBreakdown(queryParams);
      return result.data.items;
    },
    {
      ...QUERY_CONFIG,
      enabled: Boolean(employee?.id && visible),
    }
  );

  const employeeName = getFullName(employee?.surename, employee?.first_name);

  /**
   * Enable dragging when mouse is over the header
   */
  const handleMouseOver = () => setIsDraggableDisabled(false);

  /**
   * Disable dragging when mouse leaves the header
   */
  const handleMouseOut = () => setIsDraggableDisabled(true);

  /**
   * Close the modal
   */
  const handleClose = () => onHide();

  /**
   * Process data to group by week and add summary rows
   * @param items - Array of day records to process
   * @returns Processed array with week headers and summaries
   */
  const processWeeklyData = (items?: DayRecord[]): DayRecord[] => {
    if (!Array.isArray(items)) return [];

    const groupedByWeek: Record<string, DayRecord[]> = items.reduce(
      (acc, record) => {
        const date = moment(`${activeYear}-${activeMonth}-${record.day}`);
        const weekNumber = date.clone().startOf("week").week();
        acc[weekNumber] = acc[weekNumber] || [];
        acc[weekNumber].push(record);
        return acc;
      },
      {} as Record<string, DayRecord[]>
    );

    let weekOfMonth = 0;
    return Object.keys(groupedByWeek)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .reduce((result: DayRecord[], weekNumber) => {
        const weekData = groupedByWeek[weekNumber].sort(
          (a, b) => Number(a.day) - Number(b.day)
        );

        // Week header
        result.push({
          ...weekData[0],
          title: intl.formatMessage({
            id: `app.scheduling.table.weekNumber${weekOfMonth}`,
          }),
          id: `week-${weekNumber}`,
          day: weekNumber,
          titleRow: true,
        });

        // Week data
        result.push(...weekData);

        // Week summary
        const weekSummary: DayRecord = weekData.reduce(
          (sum, day) => ({
            regular_minutes:
              (sum.regular_minutes || 0) + (day.regular_minutes || 0),
            extra_minutes_rate1:
              (sum.extra_minutes_rate1 || 0) + (day.extra_minutes_rate1 || 0),
            extra_minutes_rate2:
              (sum.extra_minutes_rate2 || 0) + (day.extra_minutes_rate2 || 0),
            extra_minutes_rate3:
              (sum.extra_minutes_rate3 || 0) + (day.extra_minutes_rate3 || 0),
            extra_minutes_rate4:
              (sum.extra_minutes_rate4 || 0) + (day.extra_minutes_rate4 || 0),
            saturday_hours:
              (sum.saturday_hours || 0) + (day.saturday_hours || 0),
            holiday_hours: (sum.holiday_hours || 0) + (day.holiday_hours || 0),
            id: sum.id,
            day: sum.day,
          }),
          {
            regular_minutes: 0,
            extra_minutes_rate1: 0,
            extra_minutes_rate2: 0,
            extra_minutes_rate3: 0,
            extra_minutes_rate4: 0,
            saturday_hours: 0,
            holiday_hours: 0,
            id: `week-summary-${weekNumber}`,
            day: intl.formatMessage({ id: "app.salary.week_summary" }),
          }
        );

        // Add the summary row properties
        weekSummary.summaryRow = true;
        result.push(weekSummary);

        weekOfMonth++;
        return result;
      }, []);
  };

  // Process and group data by week with summaries
  const groupedData = useMemo<DayRecord[]>(
    () => processWeeklyData(data),
    [data, activeYear, activeMonth, intl]
  );

  /**
   * Generate table columns configuration
   * @returns Array of column definitions for the table
   */
  const getColumns = () => {
    type HourColumnKey =
      | "regular_minutes"
      | "extra_minutes_rate1"
      | "extra_minutes_rate2"
      | "extra_minutes_rate3"
      | "extra_minutes_rate4"
      | "saturday_hours"
      | "holiday_hours";

    const hourColumns: HourColumnKey[] = [
      "regular_minutes",
      "extra_minutes_rate1",
      "extra_minutes_rate2",
      "extra_minutes_rate3",
      "extra_minutes_rate4",
      "saturday_hours",
      "holiday_hours",
    ];

    const hourColumnDefs = hourColumns.map((key) => ({
      title: intl.formatMessage({ id: `app.salary.${key}` }),
      dataIndex: key,
      key,
      render: formatMinutesToHours,
      onCell: (record: DayRecord) => ({ colSpan: record.titleRow ? 0 : 1 }),
    }));

    return [
      {
        title: intl.formatMessage({ id: "app.salary.day_in_month" }),
        dataIndex: "day",
        key: "day",
        render: (text: string | number, record: DayRecord) => {
          if (record.titleRow) return <strong>{record.title}</strong>;
          if (record.summaryRow)
            return (
              <strong>
                {intl.formatMessage({ id: "app.salary.week_summary" })}
              </strong>
            );
          if (record.day_breakdown) {
            return (
              <Popover
                placement="top"
                title={null}
                trigger="hover"
                destroyTooltipOnHide
                content={
                  <DayBreakdownTable
                    breakdown={record.day_breakdown}
                    intl={intl}
                  />
                }
              >
                <span>{text}</span>
              </Popover>
            );
          }
          return text;
        },
        onCell: (record: DayRecord) => ({
          colSpan: record.titleRow ? 9 : record.summaryRow ? 2 : 1,
          style:
            record.titleRow || record.summaryRow
              ? { textAlign: "center" as const }
              : {},
        }),
      },
      {
        title: intl.formatMessage({ id: "app.salary.day_name" }),
        dataIndex: "day",
        key: "dayName",
        render: (day: string | number, record: DayRecord) => {
          if (record.titleRow || record.summaryRow) return null;
          return moment(`${activeYear}-${activeMonth}-${day}`).format("dddd");
        },
        onCell: (record: DayRecord) => ({
          colSpan: record.titleRow || record.summaryRow ? 0 : 1,
        }),
      },
      ...hourColumnDefs,
      {
        title: intl.formatMessage({ id: "app.salary.total_hours" }),
        key: "total_hours",
        render: (_: any, record: DayRecord) => {
          if (record.titleRow) return null;
          const totalMinutes = hourColumns.reduce(
            (sum, col) => sum + (record[col] || 0),
            0
          );
          return <strong>{formatMinutesToHours(totalMinutes)}</strong>;
        },
        onCell: (record: DayRecord) => ({ colSpan: record.titleRow ? 0 : 1 }),
      },
    ];
  };

  // Styles
  const modalStyles = {
    wrapper: { top: 20, maxWidth: 1400 },
    body: { padding: 0, overflowX: "auto" as const },
    header: {
      width: "100%",
      cursor: "move",
      height: "50px",
      position: "absolute",
      left: 0,
      top: 0,
    } as React.CSSProperties,
    content: { padding: "40px 20px", overflowX: "auto" as const },
  };

  return (
    <Modal
      closable={false}
      footer={null}
      maskClosable={false}
      title={null}
      width="90vw"
      style={modalStyles.wrapper}
      styles={{ body: modalStyles.body }}
      open={visible}
      destroyOnClose
      modalRender={(modal) => (
        <Draggable disabled={isDraggableDisabled}>{modal}</Draggable>
      )}
    >
      <div>
        <div
          style={modalStyles.header}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        >
          <Button
            style={{ position: "absolute", left: 10, top: 10 }}
            type="text"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={handleClose}
          />
        </div>

        <div style={modalStyles.content}>
          <div
            style={{ display: "flex", flexDirection: "column", width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div>
                <Title
                  level={1}
                  style={{ fontSize: "30px", fontWeight: 500, margin: 0 }}
                >
                  {`${intl.formatMessage({
                    id: "app.salary.order_analysis_title",
                  })} ${activeMonth}/${activeYear}`}
                </Title>
                <Title level={3} style={{ margin: "0 0 10px 0" }}>
                  {employeeName}
                </Title>
              </div>
            </div>
            <Table
              loading={isLoading}
              bordered
              dataSource={groupedData}
              rowKey={(record) => record.id || `day-${record.day}`}
              columns={getColumns()}
              pagination={false}
              style={{ padding: "20px 0" }}
              rowClassName={(record) =>
                record.titleRow ? "week_number_row" : ""
              }
              scroll={{ y: window.innerHeight - 356, x: "max-content" }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AnalysisHoursModal;
